import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Skeleton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ResourceDetailsLayout from "../layout/ResourceDetailsLayout";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Diskinfo from "../pages/Diskinfo";
import { api } from "../services/api";
import AwsDiskinfo from "../pages/AwsDiskinfo";
import AdditionalTag from "./additionalTag";
import AwsAdditionalTag from "./AwsAdditionalTags";
import PropTypes from "prop-types";
import DynamicListeners from "./alb/DynamicListeners";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useLocation } from "react-router-dom";
import DynamicRules from "../pages/sgrule/DynamicRules";
import InstanceResize from "./instanceresize/InstanceResize";
import RefreshIcon from "@mui/icons-material/Refresh";

// Helper for the automation cards
const TaskCard = ({ title, description }) => (
  <Card sx={{ minWidth: 275, margin: 1 }}>
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AccessTimeIcon />
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Typography variant="body2" color="primary" sx={{ cursor: "pointer" }}>
        Select &gt;
      </Typography>
    </CardContent>
  </Card>
);

const ApplicationNameDetails = ({ resource, onCloseDrawer, resourceTypeId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [tab, setTab] = useState(0);
  const [tags, setTags] = useState([{ name: "", value: "" }]);
  const [resourceDetails, setResourceDetails] = useState([]);
  const [viewResponse, setViewResponse] = useState({});
  const [diskDetails, setDiskDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(true);

  const selectProvider = localStorage.getItem("selectedProvider");

  // Logic Monitor States
  const [logicMonitorLoading, setLogicMonitorLoading] = useState(false);
  const [logicMonitorError, setLogicMonitorError] = useState("");

  // Ivanti Agent Installation States
  const [ivantiStatus, setIvantiStatus] = useState("Unknown");
  const [ivantiProcessLoading, setIvantiProcessLoading] = useState(false);
  const [ivantiStatusLoading, setIvantiStatusLoading] = useState(false);
  const [ivantiError, setIvantiError] = useState("");

  const location = useLocation();
  const { resourceName } = location.state || {};

  const isEc2 =
    resource?.resourceName?.toUpperCase() === "EC2" ||
    resourceName?.toUpperCase() === "EC2";

  // Helper function to get platform from resource details
  const getPlatformFromDetails = () => {
    if (!resourceDetails || resourceDetails.length === 0) return null;
    const platformDetail = resourceDetails.find(
      (detail) =>
        detail.fieldName?.toLowerCase().includes("platform") ||
        detail.fieldName?.toLowerCase().includes("operating system") ||
        detail.fieldName?.toLowerCase().includes("os")
    );
    return platformDetail?.fieldData || null;
  };

  const isWindowsPlatform = getPlatformFromDetails()?.toLowerCase().includes("windows");

  // --- Logic Monitor Handlers ---
  const handleLogicMonitorRedirect = async () => {
    if (!viewResponse?.awsInstanceId) return;
    setLogicMonitorLoading(true);
    try {
      const correlationId = crypto.randomUUID();
      const response = await api.get(
        `/api/LogicMonitor/LMResourceURL/${viewResponse.awsInstanceId}`,
        {
          headers: {
            "X-CORRELATION-ID": correlationId,
          },
        }
      );

      if (response.data?.treeNodesUrl) {
        const newTab = window.open(response.data.treeNodesUrl, "_blank");
        if (!newTab) {
          alert("Pop-up blocked! Please allow pop-ups for this site.");
        }
      }
    } catch (error) {
      console.error("Error fetching Logic Monitor URL:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to connect to Logic Monitor.";
      setLogicMonitorError(errorMessage);
    } finally {
      setLogicMonitorLoading(false);
    }
  };

  // --- Ivanti Handlers ---
  const fetchIvantiStatus = async () => {
    if (!resource?.resourceId) return;
    setIvantiStatusLoading(true);
    setIvantiError("");
    try {
      const response = await api.get(`/api/Ivanti/status/${resource.resourceId}`);
      setIvantiStatus(response.data.status || "Not Configured");
    } catch (error) {
      console.error("Error fetching Ivanti status:", error);
      setIvantiError("Failed to fetch Agent Installation status.");
    } finally {
      setIvantiStatusLoading(false);
    }
  };

  const handleIvantiProcess = async () => {
    if (!resource?.resourceId) return;
    setIvantiProcessLoading(true);
    setIvantiError("");
    try {
      const response = await api.post(`/api/Ivanti/process/${resource.resourceId}`);
      if (response.status === 202) {
        // Refresh status automatically once accepted
        fetchIvantiStatus();
      }
    } catch (error) {
      console.error("Error starting Ivanti process:", error);
      const errorMessage = error.response?.data?.message || "Failed to start Agent installation.";
      setIvantiError(errorMessage);
    } finally {
      setIvantiProcessLoading(false);
    }
  };

  // Fetch initial details and Ivanti Status
  useEffect(() => {
    if (!resource?.resourceId) return;

    const fetchDetails = async () => {
      setDetailsLoading(true);
      try {
        const response = await api.get(
          `/api/Resources/viewResourceRequest/${resource.resourceId}`,
          {
            headers: {
              "X-CORRELATION-ID": "0d89a8fe-6a3f-46e6-8bde-72daf869b07c",
            },
          }
        );
        setResourceDetails(response.data.resourceRequestDetails || []);
        setViewResponse(response.data || {});
      } catch (error) {
        console.error("Error fetching resource details:", error);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchDetails();

    // Fetch initial Ivanti status if it's an EC2 instance
    if (isEc2) {
      fetchIvantiStatus();
    }
  }, [resource, isEc2]);

  const groupedDetails = resourceDetails.reduce((acc, detail) => {
    if (!acc[detail.tabName]) {
      acc[detail.tabName] = [];
    }
    acc[detail.tabName].push(detail);
    return acc;
  }, {});

  const parentResourceID = viewResponse?.resource_Id;
  useEffect(() => {
    if (!parentResourceID) return;
    const fetchDiskDetails = async () => {
      try {
        const response = await api.get("/api/Resources/getAllDiskDetails", {
          params: { resourceReqId: parentResourceID },
          headers: {
            accept: "application/json",
          },
        });
        setDiskDetails(response.data);
      } catch (error) {
        console.error("Failed to fetch disk details", error);
      }
    };
    // fetchDiskDetails();
  }, [parentResourceID]);

  function parseFieldList(fieldListStr) {
    const noBrackets = fieldListStr.replace(/^\[/, "").replace(/\]$/, "");
    const pairs = noBrackets.split(",");

    const obj = {};
    pairs.forEach((pair) => {
      const [key, value] = pair.replaceAll('"', "").split(":");
      if (key && value) {
        obj[key.trim()] = value.trim();
      }
    });
    return obj;
  }

  if (!resource) return <Box>No resource selected</Box>;
  const isValidIP = ip => ip !== null && ip !== undefined && ip !== "" && ip !== "N/A";

  return (
    <ResourceDetailsLayout
      selectedTab={tab}
      setSelectedTab={setTab}
      resource={resource}
      onCloseDrawer={onCloseDrawer}
      isVMResizeEnabled={viewResponse?.isVMResizeEnabled}
      isResourceAddedFromCMP={viewResponse?.isResourceAddedFromCMP}
    >
      {tab === 0 && (
        <Box sx={{ fontWeight: 400 }}>
          {/* Skeleton Loading */}
          {detailsLoading ? (
            <Box>
              {[...Array(5)].map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "260px 1fr",
                    alignItems: "center",
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="80%" height={24} />
                </Box>
              ))}
              {/* Section Headers Skeleton */}
              {[...Array(3)].map((_, index) => (
                <Box key={`section-${index}`} sx={{ mt: 3 }}>
                  <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
                  {[...Array(2)].map((_, subIndex) => (
                    <Box
                      key={subIndex}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        alignItems: "center",
                        py: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="70%" height={20} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ) : (
            <>
              {selectProvider.toUpperCase() !== "AWS" &&
                diskDetails.map((detail, index) => (
                  <Typography key={index} sx={{ color: 'text.primary' }}>
                    {detail.DiskOption}: {detail.diskType}, {detail.redundency}
                  </Typography>
                ))}

              {/* Grouped Details */}
              {[
                "General",
                "Disks",
                "Networking",
                ...Object.keys(groupedDetails || {}).filter(
                  (tabName) =>
                    !["General", "Tag", "Disks", "Networking"].includes(tabName)
                ),
              ]
                .filter(
                  (tabName) =>
                    groupedDetails[tabName] && groupedDetails[tabName].length > 0
                )
                .map((tabName) => (
                  <Box key={tabName}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1, color: "text.primary", mt: 1 }}
                >
                  {tabName}
                </Typography>

                {(groupedDetails[tabName] || [])
                  .filter((detail) => detail.fieldName !== "ivantiConfiguration")
                  .map((detail) => {
                    const isListeners = detail.fieldName === "Listeners";
                    const isRules =
                      detail.fieldName === "InboundRules" ||
                      detail.fieldName === "OutboundRules";

                    return (
                      <Box key={detail.requestId || detail.fieldName} sx={{ mb: 1 }}>
                        {isListeners ? (
                          <Box sx={{ mt: 2 }}>
                            <DynamicListeners
                              fieldData={detail.fieldData}
                              tablefrom={true}
                            />
                          </Box>
                        ) : isRules ? (
                          <Box sx={{ mt: 3 }}>
                            <DynamicRules
                              fieldData={detail.fieldData}
                              title={detail.fieldName}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "260px 1fr",
                              alignItems: "center",
                              py: 1,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", fontWeight: 400 }}
                            >
                              {detail.fieldName}:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? "#90caf9" : "#1976d2",
                                fontWeight: 400,
                                wordBreak: "break-word",
                              }}
                            >
                              {detail.fieldData || "N/A"}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                {/* AWS Resource Identifier inside General */}
                {tabName === "General" &&
                  selectProvider?.toUpperCase() === "AWS" &&
                  viewResponse?.awsInstanceId && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        alignItems: "center",
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 400,
                        }}
                      >
                        AWS Resource Identifier:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? "#90caf9" : "#1976d2",
                          fontWeight: 400,
                        }}
                      >
                        {viewResponse.awsInstanceId}
                      </Typography>
                    </Box>
                  )}
              </Box>
            ))}

          {/* Ivanti Configuration Section */}
          {(() => {
            const ivantiDetail = resourceDetails?.find(
              (d) => d.fieldName === "ivantiConfiguration"
            );
            if (!ivantiDetail?.fieldData) return null;

            let parsed = null;
            try {
              parsed =
                typeof ivantiDetail.fieldData === "string"
                  ? JSON.parse(ivantiDetail.fieldData)
                  : ivantiDetail.fieldData;
            } catch (e) {
              console.error("Failed to parse ivantiConfiguration", e);
            }

            if (!parsed || Object.keys(parsed).length === 0) return null;

            return (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, mt: 2, color: "text.primary" }}
                >
                  Ivanti Configuration
                </Typography>

                {Object.entries(parsed).map(([key, val]) => {
                  let displayValue = "N/A";

                  if (Array.isArray(val)) {
                    displayValue = val.map((v) => v?.text || v?.value || v).join(", ");
                  } else if (val && typeof val === "object") {
                    displayValue = val.text || val.value || "N/A";
                  } else if (val !== null && val !== undefined) {
                    displayValue = String(val);
                  }

                  const formattedLabel = key.replace(/([A-Z])/g, " $1").trim();

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        alignItems: "center",
                        py: 1, mb: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {formattedLabel}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? "#90caf9" : "#1976d2",
                          fontWeight: 400,
                          wordBreak: "break-word",
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}

          {/* Logic Monitor & Ivanti Details - For EC2 Resources */}
          {resourceName?.toLowerCase() === "ec2" &&
            selectProvider?.toUpperCase() === "AWS" &&
            viewResponse?.isResourceAddedFromCMP === true &&
            viewResponse?.awsInstanceId &&
            viewResponse?.resourceStatus?.toLowerCase() !== "terminated" && (
              <>
                {/* Logic Monitor */}
                <Box
                  sx={{
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "260px 1fr",
                      alignItems: "center",
                      py: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                      }}
                    >
                      Logic Monitor
                    </Typography>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        logicMonitorLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <OpenInNewIcon />
                        )
                      }
                      disabled={logicMonitorLoading}
                      onClick={handleLogicMonitorRedirect}
                      sx={{ width: "fit-content" }}
                    >
                      Check Status in Logic Monitor
                    </Button>
                  </Box>

                  {logicMonitorError && (
                    <Box sx={{ width: "100%", mt: 2 }}>
                      <Alert
                        severity="error"
                        sx={{ width: "100%", boxSizing: "border-box" }}
                      >
                        {logicMonitorError}
                      </Alert>
                    </Box>
                  )}
                </Box>

                {/* Agent Installation (Ivanti) - Only for Windows Platform & Running Status */}
                {isWindowsPlatform && viewResponse?.resourceStatus?.toLowerCase() === "running" && (
                  <Box
                    sx={{
                      py: 2,
                      mb: 3,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "260px 1fr",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        Agent Installation
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Button
                          variant="contained"
                          sx={{ p: "7px" }}
                          size="small"
                          onClick={handleIvantiProcess}
                          disabled={
                            ivantiProcessLoading ||
                            ivantiStatus === "In Progress" ||
                            ivantiStatus === "Completed"
                          }
                        >
                          {ivantiProcessLoading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            "Trigger Ivanti Workflow"
                          )}
                        </Button>

                        <Tooltip title="Refresh Ivanti Status">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={fetchIvantiStatus}
                            disabled={ivantiStatusLoading}
                            sx={{ minWidth: "auto", p: "4px 8px" }}
                          >
                            {ivantiStatusLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </Button>
                        </Tooltip>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color:
                              ivantiStatus === "Completed"
                                ? "success.main"
                                : ivantiStatus === "Failed"
                                  ? "error.main"
                                  : ivantiStatus === "In Progress"
                                    ? "warning.main"
                                    : "text.secondary",
                          }}
                        >
                          Status: {ivantiStatus}
                        </Typography>
                      </Box>
                    </Box>

                    {ivantiError && (
                      <Box sx={{ width: "100%", mt: 2 }}>
                        <Alert
                          severity="error"
                          sx={{ width: "100%", boxSizing: "border-box" }}
                        >
                          {ivantiError}
                        </Alert>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}

          {/* IP Address Details */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 3 }}>
              {(isValidIP(viewResponse.publicIP) ||
                isValidIP(viewResponse.privateIP)) && (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 1, mt: 2, color: "text.primary" }}
                  >
                    IP Addresses
                  </Typography>
                )}

              {isValidIP(viewResponse.publicIP) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "260px 1fr",
                    alignItems: "center",
                    py: 1, mb: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Public IPv4 Address:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: isDark ? "#90caf9" : "#1976d2" }}
                  >
                    {viewResponse.publicIP}
                  </Typography>
                </Box>
              )}

              {isValidIP(viewResponse.privateIP) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "260px 1fr",
                    alignItems: "center",
                    py: 1, mb: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Private IPv4 Address:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: isDark ? "#90caf9" : "#1976d2" }}
                  >
                    {viewResponse.privateIP}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Price Details - For Non-EC2 Resources (ALB, NLB, Route 53, RDS, etc.) */}
          {!isEc2 && viewResponse?.isResourceAddedFromCMP && (
            <Box sx={{ mb: 2, mt: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}
              >
                Price Details
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", fontWeight: 600 }}
                >
                  Total Forecasted Price:
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: isDark ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd",
                    border: "1px solid",
                    borderColor: isDark ? "rgba(144, 202, 249, 0.3)" : "#90caf9",
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: isDark ? "#90caf9" : "#1565c0",
                    }}
                  >
                    {viewResponse?.totalPrice
                      ? `$${Number(viewResponse.totalPrice).toFixed(2)}`
                      : "N/A"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? "#90caf9" : "#1565c0",
                      fontWeight: 500,
                    }}
                  >
                    {viewResponse?.totalPrice ? " / mo" : " "}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Price Details - Single Line */}
          {viewResponse?.isResourceAddedFromCMP && isEc2 && (
            <Box sx={{ mb: 2, mt: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Price Details
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", fontWeight: 600 }}
                  >
                    Total Price (Resource + Additional Disk):
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: isDark ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd",
                      border: "1px solid",
                      borderColor: isDark ? "rgba(144, 202, 249, 0.3)" : "#90caf9",
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? "#90caf9" : "#1565c0",
                      }}
                    >
                      {viewResponse?.totalPrice
                        ? `$${Number(viewResponse.totalPrice).toFixed(2)}`
                        : "N/A"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#90caf9" : "#1565c0",
                        fontWeight: 500,
                      }}
                    >
                      / mo
                    </Typography>
                  </Box>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, height: 16, alignSelf: "center" }}
                />

                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Resource Price:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", fontWeight: 600 }}
                  >
                    {viewResponse?.resourceTotalPrice
                      ? `$${Number(viewResponse.resourceTotalPrice).toFixed(2)}`
                      : "N/A"}
                  </Typography>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, height: 16, alignSelf: "center" }}
                />

                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Additional Disk Price:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        viewResponse?.additionalDiskTotalPrice &&
                          Number(viewResponse.additionalDiskTotalPrice) > 0
                          ? "text.primary"
                          : "text.secondary",
                      fontWeight: 600,
                    }}
                  >
                    {viewResponse?.additionalDiskTotalPrice &&
                      Number(viewResponse.additionalDiskTotalPrice) > 0
                      ? `$${Number(viewResponse.additionalDiskTotalPrice).toFixed(2)}`
                      : "$0.00"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
            </>
          )}
        </Box>
      )}

      {tab === 1 &&
        (selectProvider?.toUpperCase() === "AWS" ? (
          <AwsAdditionalTag
            resourceOverview={viewResponse}
            resourceTypeId={resourceTypeId}
            resourceStatus={resource.resourceStatus}
          />
        ) : (
          <AdditionalTag
            resourceOverview={viewResponse}
            resourceTypeId={resourceTypeId}
          />
        ))}

      {tab === 2 && (
        <Box sx={{ fontWeight: 400 }}>
          {selectProvider?.toUpperCase() !== "AWS" ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "text.primary" }}>Storage Type</TableCell>
                    <TableCell sx={{ color: "text.primary" }}>Redundancy Name</TableCell>
                    <TableCell sx={{ color: "text.primary" }}>Disk Option</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {resource.storageDetails || "Standard SSD Managed Disks"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {resource.redudencyName || "Locally Redundant Storage Disks"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {resource.diskOption || "E15 LRS Disk(256 GiB)"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            ""
          )}
          {selectProvider?.toUpperCase() === "AWS" ? (
            <AwsDiskinfo
              resourceOverview={viewResponse}
              resourceTypeId={resourceTypeId}
            />
          ) : (
            <Diskinfo
              resourceOverview={viewResponse}
              resourceTypeId={resourceTypeId}
            />
          )}
        </Box>
      )}

      {tab === 3 && (
        <InstanceResize
          resourceOverview={viewResponse}
          resourceTypeId={resourceTypeId}
        />
      )}
    </ResourceDetailsLayout>
  );
};

TaskCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

ApplicationNameDetails.propTypes = {
  resource: PropTypes.shape({
    resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    applicationName: PropTypes.string,
    resourceStatus: PropTypes.string,
    storageDetails: PropTypes.string,
    redudencyName: PropTypes.string,
    diskOption: PropTypes.string,
  }),
  onCloseDrawer: PropTypes.func.isRequired,
  resourceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ApplicationNameDetails;